import type { CSSProperties } from "react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Table.module.scss";
import { cssUnit } from "../types";
import clsx from "clsx";
import Pagination from "./Pagination";
import { ProgressLinear } from "../Progress";
import { useOnClickOutside } from "../../utils";

export type TColumn<
  TData,
  TFieldKey extends keyof TData = keyof TData,
  TField = TData[TFieldKey],
> = {
  field?: TFieldKey;
  header?: string;
  width?: string | number;
  editable?: boolean;
  cellRenderer?: (field: TField, row: TData, index: number) => React.ReactNode;
  editInputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  divider?: "before" | "after";
  sortable?: boolean;
};

function getCellData<T>(
  col: TColumn<T>,
  row: T,
  index: number,
): React.ReactNode {
  if (col.field) {
    if (col.cellRenderer) return col.cellRenderer(row[col.field], row, index);
    else return row[col.field] as any;
  } else {
    if (col.cellRenderer)
      return col.cellRenderer(undefined as T[keyof T], row, index);
    else return "";
  }
}

export type TableProps<TData> = {
  data: TData[];
  columns: TColumn<TData>[];
  style?: CSSProperties;
  className?: string;
  onGetData?: (pageIndex: number, size: number) => void;
  onChangeData?: (data: TData[], row: TData, rowIndex: number) => void;
  loading?: boolean;
} & (
  | {
      pagination?: true | "client";
      dataSize?: never;
    }
  | {
      pagination?: "server";
      dataSize: number;
    }
);

export interface CellProps<TData> {
  column: TColumn<TData>;
  children: React.ReactNode;
  onChange: (value: TData[NonNullable<TColumn<TData>["field"]>]) => void;
}

function Cell<TData>(props: CellProps<TData>) {
  const { children, column, onChange } = props;
  const { editable, width, divider } = column;
  const [editing, setEditing] = useState(false);
  const handleDataChange = (value) => {
    setEditing(false);
    onChange(value);
  };
  const inputRef = useRef<HTMLInputElement>(null);
  useOnClickOutside(inputRef, () => {
    if (editing) {
      handleDataChange(inputRef.current?.value);
    }
  });
  return (
    <td
      style={{
        minWidth: width ? cssUnit(width) : undefined,
      }}
      onDoubleClick={editable ? () => setEditing(true) : undefined}
      className={clsx(
        styles["cell"],
        editable && styles["cell--editable"],
        divider && styles[`cell--divider-${divider}`],
      )}
    >
      {editing ? (
        <input
          ref={inputRef}
          defaultValue={
            ["string", "number"].includes(typeof children)
              ? (children as string)
              : ""
          }
          autoFocus
          onKeyDown={(e) => {
            if (e.key == "Enter") {
              handleDataChange((e.target as HTMLInputElement).value);
            } else if (e.key == "Escape") {
              setEditing(false);
            }
            column.editInputProps?.onKeyDown?.(e);
          }}
          onBlur={(e) => {
            if (String(children) !== e.target.value) {
              handleDataChange(e.target.value);
            }
            column.editInputProps?.onBlur?.(e);
          }}
          {...column.editInputProps}
        />
      ) : (
        children
      )}
    </td>
  );
}

function Table<TData>(props: TableProps<TData>) {
  const {
    columns,
    data,
    style,
    className,
    pagination,
    onGetData,
    dataSize,
    onChangeData,
    loading,
  } = props;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const handleDataChange = function (column: TColumn<TData>, rowIndex: number) {
    return (value) => {
      if (!column.field) return;
      const newData = [...data];
      newData[rowIndex] = { ...newData[rowIndex], [column.field]: value };
      onChangeData?.(newData, newData[rowIndex], rowIndex);
    };
  };

  const rows = useMemo(() => {
    if (pagination) {
      if (pagination === true || pagination === "client") {
        const index = page - 1;
        return data.slice(index * pageSize, index * pageSize + pageSize);
      } else {
        return data;
      }
    } else {
      return data;
    }
  }, [data, page, pageSize]);

  useEffect(() => {
    if (pagination === "server") {
      onGetData?.(page, pageSize);
    }
  }, [page]);

  return (
    <div
      className={clsx(
        styles["wrapper"],
        loading && styles["wrapper--loading"],
        className,
      )}
      style={style}
    >
      <div>
        <table className={styles["table"]}>
          <tr className={styles["table__header"]}>
            {columns.map((column, index) => (
              <th
                key={index}
                className={clsx(
                  styles["table__header__cell"],
                  column.divider &&
                    styles[`table__header__cell--divider-${column.divider}`],
                  column.sortable && styles[`table__header__cell--sortable`],
                )}
                style={{
                  minWidth: column.width ? cssUnit(column.width) : undefined,
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={styles["table__row"]}>
              {columns.map((column, headerIndex) => (
                <Cell
                  column={column}
                  onChange={handleDataChange(column, data.indexOf(row))}
                >
                  {getCellData(column, row, data.indexOf(row))}
                </Cell>
              ))}
            </tr>
          ))}
        </table>
      </div>
      {loading && <ProgressLinear />}
      {pagination && (
        <Pagination
          dataSize={
            pagination === true || pagination === "client"
              ? data.length
              : dataSize || 0
          }
          value={page}
          onChange={(value, size) => {
            setPage(value);
            if (size !== pageSize) {
              setPageSize(size);
              setPage(1);
            }
          }}
        />
      )}
    </div>
  );
}

export default Table;
